/**
 * The token command menu's **host** — `projects/token-command-menu.md` 2.3-2.5 and 3.1-3.2: the
 * one place the {@link TokenCommandMenu} view meets the live encounter. It builds the Phase 1
 * action model ({@link useUnitActionModel}, the mech's one derivation and set of handlers), turns it into {@link commandMenuTree}, runs each chosen row's {@link commandMenuRowEffect},
 * and reports a hovered row's would-be aim ({@link commandMenuAimFor}) as the board's hover preview.
 *
 * It also holds the fire card's one piece of local state, the {@link FireCommit}: a Skirmish's
 * mount or a Barrage's confirmed mounts, before the first shot reaches the server (R7). From the
 * first shot on, the tree reads the unit's server-synced lock instead, and the commit is dropped
 * once that lock closes or the action is marked used.
 *
 * **The fire sequence runs in targeting mode** (the "Barrage fires from targeting mode" ruling):
 * committing the mounts (the Barrage confirm, a Skirmish mount) arms the first fireable weapon at
 * once, and after each shot the host arms the next unfired one ({@link nextFireWeapon}), so the
 * player only clicks targets. The fire card is a menu screen like any other: while a shot is armed
 * the card carries the shot's controls under its weapons.
 * When the last fireable weapon fires, or the player stops or ends early, the lock closes, the
 * leftover weaponless aim is cleared, and the menu lands (`homeOf`). Only a sequence the menu
 * started (`seq`) is driven this way; a fixed-target Skirmish armed elsewhere (the Overwatch take,
 * the GM advisor's plan, both fired from the sheet's `SkirmishChooser`) is left alone.
 *
 * Three declarations the retired sheet grid carried live here too (Phase 4): Boost's opt-in riders
 * on Boost's form card ({@link BoostRiderControls}), and, in targeting mode's panel slot, Ram's
 * knock-back toggle ({@link RamControls}) and a directed deploy's sections left with its back one
 * pick ({@link SitesControls}).
 *
 * Everything mid-action reads the server-synced lock (3.3): the fire card, the live Full Tech card
 * and the grapple follow-up (the `Grappled` marker) render off the encounter state, so a menu
 * reopened, reloaded or mounted on a second client lands on the live card.
 *
 * `combatant` is the raw encounter entry: the host only mounts for a unit the viewer controls AND
 * receives unmasked ({@link commandMenuStateFor}; a control grant without the visibility grant
 * opens no menu, `decisions/0179`). `menuState` says whether its rows
 * are enabled or explorable-and-disabled (2c).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { activeAttack, type Combatant, type EncounterState } from "@lancer-vtt/domain";
import { useCompendiumContext } from "../CompendiumProvider";
import { buildStopBarrageAsSkirmishIntent } from "./attackIntent";
import type { MovePreview } from "./boostRing";
import { popDirectedDeployPick, type ActionDraft } from "./combatInspector";
import { commandMenuTree, type CommandMenuScreenId, type FireAction, type FireCommit, type FireExit } from "./commandMenu";
import { commandMenuChooserFor, commandMenuConfirmEffect, fireCommitSpent, commandMenuPreviewAim, commandMenuPreviewMove, commandMenuRowEffect, type CommandMenuRowEffect, type CommandMenuState } from "./commandMenuActions";
import { AttackAimControls } from "./combatWindows/AttackAimControls";
import { DisarmMineControls, JockeyControls, MountControls, RamControls, RideControls, SearchControls, SelfDestructControls, SitesControls, StabilizeControls, TechAimControls, techOptionIsAttack } from "./combatWindows/choosers";
import { PrepareControls } from "./combatWindows/PrepareChooser";
import { BoostRiderControls } from "./combatWindows/unitActions";
import { useUnitActionModel } from "./combatWindows/useUnitActionModel";
import { FootButton, TokenCommandMenu } from "./TokenCommandMenu";
import { nextFireWeapon, type PendingArm } from "./fireSequence";
import { targetingBanner } from "./TargetingMode";
import type { UseEncounter } from "./useEncounter";

/** The armed aim, when it is this unit's shot for the fire card's attack. */
function fireAimOf(aim: ActionDraft | null, unitId: string, action: FireAction | undefined) {
  if (!aim || aim.attackerId !== unitId || !action || aim.mode !== action) return undefined;
  return aim.mode === "skirmish" || aim.mode === "barrage" ? aim : undefined;
}

export function TokenCommandMenuHost({
  combatant,
  menuState,
  state,
  actorId,
  encounter,
  aim,
  setAim,
  onPreviewAim,
  onPreviewMove,
  targetingSlot = null,
  onActiveScreen,
  onStrip,
}: {
  combatant: Combatant;
  menuState: CommandMenuState;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  /** The hovered row's would-be aim, or `null`: the board paints its reach and target rings. */
  onPreviewAim: (aim: ActionDraft | null) => void;
  /** The hovered Boost / Disengage row, or `null`: the board paints the cells the move would reach. */
  onPreviewMove: (move: MovePreview | null) => void;
  /** Targeting mode's panel slot (`TargetingMode`'s `slotRef`), where the fire sequence's panel goes. */
  targetingSlot?: HTMLElement | null;
  /** The menu's active screen (`null` once it unmounts): targeting mode drops its CANCEL on the fire card. */
  onActiveScreen?: (screen: CommandMenuScreenId | null) => void;
  /** Fold the menu to its one-line strip while the unit panel is expanded (`TokenCommandMenu`'s `onStrip`). */
  onStrip?: (() => void) | undefined;
}) {
  const { compendium, overlay, loadingOverlay } = useCompendiumContext();
  const model = useUnitActionModel({ combatant, state, actorId, isGm: encounter.isGm, canControl: true, compendium, overlay, loadingOverlay, encounter, aim, setAim });
  const [commit, setCommit] = useState<FireCommit | null>(null);
  // The menu started this fire sequence, so it auto-arms each next weapon and shows the panel.
  const [seq, setSeq] = useState(false);
  const [autoArm, setAutoArm] = useState<PendingArm | null>(null);
  const turn = state.activation.turn;
  const lock = turn.phase === "acting" && turn.actor === combatant.id ? activeAttack(state.activation) : null;
  const fireLockOpen = lock?.action === "skirmish" || lock?.action === "barrage";
  const chooser = commandMenuChooserFor(aim, combatant.id);
  // The commit lasts until the server's lock takes over and closes, or the action is spent without
  // one (a one-weapon Skirmish, a Superheavy Barrage). A spent commit is dropped from the tree in
  // the render it is spent, so the fire card never re-reads it as a fresh commitment.
  const wasLive = useRef(false);
  const actionUses = turn.phase === "acting" && turn.actor === combatant.id ? turn.budget.actionUses : undefined;
  const used = fireCommitSpent({ commit, model, actionUses });
  const liveCommit = commit && !fireLockOpen && (wasLive.current || used) ? null : commit;
  // The GM's override mode (`decisions/0209`) opens the menu under its own headline.
  const override = menuState.kind === "enabled" && menuState.headline === "OVERRIDE";
  const tree = commandMenuTree({ model, combatant, state, lock, commit: liveCommit, chooser, menuState, override });
  const fire = tree.screens.fire?.fire;
  const fireAim = fireAimOf(aim, combatant.id, fire?.action);
  const live = fire?.live === true;
  useEffect(() => {
    if (commit && !live && (wasLive.current || used)) setCommit(null);
    wasLive.current = live;
  }, [commit, live, used]);

  // A menu that goes away takes its hover preview with it.
  useEffect(
    () => () => {
      onPreviewAim(null);
      onPreviewMove(null);
    },
    [onPreviewAim, onPreviewMove],
  );

  const run = (effect: CommandMenuRowEffect): CommandMenuRowEffect["kind"] => {
    if (effect.kind === "none") return "none";
    effect.run();
    return effect.kind;
  };

  const fireWeapon = (weaponKey: string, action: FireAction) => {
    const keep = fireAimOf(aim, combatant.id, action);
    const accuracy = keep?.weaponId === weaponKey ? keep.accuracy : 0;
    setSeq(true);
    if (action === "barrage") {
      setAim({ attackerId: combatant.id, mode: "barrage", weaponId: weaponKey, profileId: "", accuracy, mounts: fire?.groups.map((g) => g.key) ?? commit?.mounts ?? [] });
    } else {
      setAim({ attackerId: combatant.id, mode: "skirmish", weaponId: weaponKey, profileId: "", accuracy });
    }
  };
  const exitFire = (exit: FireExit) => {
    setAutoArm(null);
    if (fireAim) setAim(null);
    if (exit === "cancel") setCommit(null);
    else if (exit === "stop_as_skirmish") encounter.sendIntent(buildStopBarrageAsSkirmishIntent(combatant.id));
    else model.endAttack();
  };
  const armedWeapon = fireAim && fireAim.weaponId !== "" ? fireAim.weaponId : undefined;
  // A shot was sent (`fireAt` keeps the aim, weaponless, while more can follow).
  const weaponless = fireAim !== undefined && fireAim.weaponId === "";

  // After each shot of a menu-driven sequence, arm the next unfired weapon.
  const prevArmed = useRef(armedWeapon);
  useEffect(() => {
    const was = prevArmed.current;
    prevArmed.current = armedWeapon;
    if (seq && was !== undefined && armedWeapon === undefined && weaponless) setAutoArm({ exclude: was, firedLen: fire?.fired.length ?? 0 });
  }, [seq, armedWeapon, weaponless, fire]);
  useEffect(() => {
    if (!autoArm || !fire) return;
    // Disarmed (Esc) or re-aimed by hand since the shot: nothing to follow up.
    if (autoArm.exclude !== undefined && !weaponless) return setAutoArm(null);
    const next = nextFireWeapon(fire, autoArm);
    if (next === null) return;
    setAutoArm(null);
    fireWeapon(next, fire.action);
  });
  // A rejected shot never echoes: stop skipping its weapon.
  const rejectionSeq = encounter.rejection?.seq;
  useEffect(() => {
    setAutoArm((a) => (a?.exclude !== undefined ? { firedLen: a.firedLen } : a));
  }, [rejectionSeq]);
  // A weapon armed before the echo that the echo blocks (its mount's pair fired): re-arm.
  useEffect(() => {
    if (!seq || !fire?.live || armedWeapon === undefined || fire.fireable.includes(armedWeapon)) return;
    const next = fire.fireable[0];
    if (next) fireWeapon(next, fire.action);
    else setAim(null);
  });
  // The sequence ends with its fire card: clear the leftover weaponless aim so targeting mode ends.
  const hadFire = useRef(fire !== undefined);
  useEffect(() => {
    const had = hadFire.current;
    hadFire.current = fire !== undefined;
    if (fire || !had) return;
    setAutoArm(null);
    if (!seq) return;
    setSeq(false);
    if (aim?.attackerId === combatant.id && (aim.mode === "skirmish" || aim.mode === "barrage") && aim.weaponId === "") setAim(null);
  });
  const techAim = aim && aim.attackerId === combatant.id && (aim.mode === "quick_tech" || aim.mode === "full_tech") ? aim : undefined;
  // A hosted chooser's controls (3.2): the chooser's form (shared with the NPC sheet's chooser),
  // committing through the model's handler. Each handler clears the aim once it sends, which closes the card.
  const chooserPanel =
    chooser === "stabilize" ? (
      <StabilizeControls combatant={combatant} repairsLeft={model.repairsLeft} onFire={model.fireStabilize} />
    ) : chooser === "search" ? (
      <SearchControls combatant={combatant} state={state} onFire={model.fireSearch} />
    ) : chooser === "disarm_mine" ? (
      <DisarmMineControls combatant={combatant} state={state} onFire={model.fireDisarmMine} />
    ) : chooser === "self_destruct" ? (
      <SelfDestructControls combatant={combatant} onFire={model.fireSelfDestruct} />
    ) : chooser === "mount" ? (
      <MountControls combatant={combatant} state={state} onFire={model.fireMount} />
    ) : chooser === "jockey" ? (
      <JockeyControls combatant={combatant} state={state} onFire={model.fireJockey} />
    ) : chooser === "ride" ? (
      <RideControls combatant={combatant} state={state} onFire={model.fireRide} />
    ) : chooser === "prepare" && aim?.mode === "prepare" ? (
      <PrepareControls combatant={combatant} state={state} actions={model.descriptors} aim={aim} setAim={setAim} onPrepare={model.firePrepare} />
    ) : undefined;

  // The armed shot's controls, on the fire card under its weapons.
  const firePanel = fireAim && armedWeapon !== undefined ? <AttackAimControls combatant={combatant} state={model.economy.state} aim={fireAim} setAim={setAim} /> : undefined;

  // Targeting mode's panel for a board pick with a declaration the banner can't carry: Ram's
  // knock-back toggle, and a directed deploy's sections left with its back-one-pick. Each carries its
  // own CANCEL, since a filled slot hides the banner's.
  const pickAim = aim && aim.attackerId === combatant.id ? aim : null;
  const pickPanelBody =
    pickAim?.mode === "ram" ? (
      <RamControls aim={pickAim} setAim={setAim} />
    ) : pickAim?.mode === "activate_sites" ? (
      <SitesControls total={pickAim.count} remaining={pickAim.count - pickAim.picks.length} canBack={pickAim.picks.length > 0} onBack={() => setAim(popDirectedDeployPick(pickAim))} />
    ) : null;
  const pickTitle = pickAim?.mode === "ram" ? "RAM" : pickAim?.mode === "activate_sites" ? (model.grantedViews.find((v) => v.actionId === pickAim.actionId)?.name ?? "DEPLOY").toUpperCase() : "";
  const pickPanel =
    pickPanelBody && targetingSlot
      ? createPortal(
          <section className="tcm-fseq" aria-label={pickTitle}>
            <header className="tcm-fseq-head">
              <span className="tcm-fseq-title">{pickTitle}</span>
            </header>
            <div className="tcm-panel tcm-fseq-controls">{pickPanelBody}</div>
            <div className="tcm-fire-foot">
              <FootButton label="CANCEL" onClick={() => setAim(null)} />
            </div>
          </section>,
          targetingSlot,
        )
      : null;

  // Boost's form card, while its Boost carries opt-in riders (the Rapid Burst Jump Jet's Fly).
  const boostRiders = model.views.find((v) => v.id === "boost")?.optInRiders ?? [];
  const boostRow = tree.screens.maneuver?.rows.find((r) => r.id === "boost");
  const boostPanel =
    tree.screens.boost && boostRow ? (
      <BoostRiderControls riders={boostRiders} blockReason={boostRow.available ? undefined : boostRow.cantAfford ? "" : (boostRow.reason ?? "")} onBoost={(choices) => model.boost(choices)} />
    ) : null;

  return (
    <>
      {pickPanel}
      <TokenCommandMenu
        tree={tree}
        unitName={combatant.name}
        headline={menuState.headline}
        // Only an aim that picks a unit (targeting mode) holds the card for its shot. A fire
        // sequence's shot does not: the fire card is the menu screen the player targets from.
        aiming={!fireAim && targetingBanner({ state, aim, legalCount: 0 }) !== null}
        onRow={(row, screen) => {
          onPreviewAim(null);
          onPreviewMove(null);
          const kind = run(commandMenuRowEffect({ row, screen, model, unitId: combatant.id, setAim }));
          // 3.1: a Skirmish mount arms its first fireable weapon and commits the fire card to it; the
          // sequence runs in targeting mode from here.
          if (kind === "target" && row.kind === "mount" && screen === "skirmish") {
            // A pilot's Fight row names its ledger key: the commit is spent when that count moves.
            setCommit({ action: "skirmish", mounts: [row.id], ...(row.usedKey ? { ledger: { key: row.usedKey, at: actionUses?.[row.usedKey] ?? 0 } } : {}) });
            setSeq(true);
          }
          return kind;
        }}
        onConfirm={(screen, picks) => {
          onPreviewAim(null);
          onPreviewMove(null);
          // 3.2: the Barrage confirm commits the mounts locally and goes straight into targeting mode
          // on the first fireable weapon (armed once the tree carries the commitment).
          if (screen === "barrage") {
            setCommit({ action: "barrage", mounts: picks });
            setSeq(true);
            setAutoArm({ firedLen: 0 });
            return "none";
          }
          return run(commandMenuConfirmEffect({ screen, picks, unitId: combatant.id, setAim }));
        }}
        onFire={fireWeapon}
        onFireExit={exitFire}
        onFullTechEnd={() => {
          if (techAim) setAim(null);
          model.endAttack();
        }}
        {...(armedWeapon !== undefined ? { armedWeapon } : {})}
        panels={{
          // Invade's ACC / DIFF, on the card its option was armed from (3.2).
          ...(techAim && techOptionIsAttack(combatant, techAim.optionId) ? { [techAim.mode]: <TechAimControls combatant={combatant} aim={techAim} setAim={setAim} /> } : {}),
          ...(chooser && chooserPanel ? { [chooser]: chooserPanel } : {}),
          ...(boostPanel ? { boost: boostPanel } : {}),
          ...(firePanel ? { fire: firePanel } : {}),
        }}
        onChooserCancel={() => setAim(null)}
        {...(onActiveScreen ? { onActiveScreen } : {})}
        {...(onStrip ? { onStrip } : {})}
        onEndTurn={() => (model.isActiveActor ? model.endTurn() : model.passTurn())}
        onPreview={(row, screen, weaponKey) => {
          // While a shot is armed, a hovered weapon previews its own range with the shot's mounts;
          // the armed one (or none) restores the armed shot's.
          if (screen === "fire" && fireAim) return onPreviewAim(row && row.id !== armedWeapon ? { ...fireAim, weaponId: row.id, profileId: "", accuracy: 0 } : null);
          onPreviewAim(row ? commandMenuPreviewAim({ row, screen, model, unitId: combatant.id, weaponKey }) : null);
          onPreviewMove(row ? commandMenuPreviewMove({ row, unitId: combatant.id, menuState }) : null);
        }}
      />
    </>
  );
}
